import { FC } from 'react';
import { Markdown } from './markdown';
import { formatToolInput } from './formatters';
import style from './audit-log.module.scss';

interface ConversationToolCall {
  name?: string;
  id?: string;
  input?: unknown;
}

interface ConversationEntry {
  timestamp?: string;
  text?: string;
  tool_calls?: ConversationToolCall[];
}

interface ConversationRow {
  key: string;
  time: string;
  kind: 'thinking' | 'tool';
  text?: string;
  tool?: string;
  input?: string;
}

const flattenConversation = (entries: unknown[]): ConversationRow[] => {
  const rows: ConversationRow[] = [];
  entries.forEach((raw, i) => {
    const entry = raw as ConversationEntry;
    const time = entry.timestamp
      ? new Date(entry.timestamp).toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
      : '—';
    if (entry.text && entry.text.trim().length > 0) {
      rows.push({
        key: `${i}-text`,
        time,
        kind: 'thinking',
        text: entry.text,
      });
    }
    (entry.tool_calls ?? []).forEach((tc, j) => {
      rows.push({
        key: `${i}-tool-${j}`,
        time,
        kind: 'tool',
        tool: tc.name,
        input: formatToolInput(tc.input),
      });
    });
  });
  return rows;
};

export const ConversationTable: FC<{ entries: unknown[] }> = ({ entries }) => {
  const rows = flattenConversation(entries);
  if (rows.length === 0)
    return <p className={style.muted}>No conversation entries.</p>;
  return (
    <table className={style.convTable}>
      <thead>
        <tr>
          <th>Time</th>
          <th>Type</th>
          <th>Content</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key}>
            <td className={style.convTime}>{r.time}</td>
            <td className={style.convType}>
              {r.kind === 'thinking' ? (
                <span
                  className={`${style.typeBadge} ${style.typeBadgeThinking}`}
                >
                  Thinking
                </span>
              ) : (
                <span className={`${style.typeBadge} ${style.typeBadgeTool}`}>
                  Tool call
                </span>
              )}
            </td>
            <td className={style.convContent}>
              {r.kind === 'thinking' ? (
                <div className={style.thinkingText}>
                  <Markdown text={r.text ?? ''} />
                </div>
              ) : (
                <div>
                  <code className={style.toolName}>{r.tool}</code>
                  {r.input && <pre className={style.toolInput}>{r.input}</pre>}
                </div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
